import { db } from '../firebase'
import { collection, getDocs, query, where } from 'firebase/firestore'

// Interface for the raw Firestore student data (flat structure)
interface FirestoreStudentData {
  id: string
  studentId?: string
  name: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  instrument?: string
  instruments?: string[]
  notes?: string
  avatar?: string
  profilePicture?: string
  status?: string
  lessonType?: 'In-person' | 'Online' | 'Hybrid'
  timeZone?: string
  // Flat address fields
  addressStreet?: string
  addressApt?: string
  addressCity?: string
  addressState?: string
  addressZip?: string
  // Flat guardian fields
  guardianName?: string
  guardianEmail?: string
  guardianPhone?: string
  guardianPreferredContact?: string
  guardianIsEmergencyContact?: boolean
  // Metadata
  createdAt?: string
  updatedAt?: string
  notesUpdatedAt?: string
}

// Interface for the transformed student data (nested structure)
export interface Student {
  id: string
  studentId?: string
  name: string
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  instrument?: string
  instruments?: string[]
  notes?: string
  avatar?: string
  profilePicture?: string
  status?: string
  lessonType?: 'In-person' | 'Online' | 'Hybrid'
  timeZone?: string
  address?: {
    street?: string
    apt?: string
    city?: string
    state?: string
    zip?: string
  }
  guardian?: {
    name?: string
    email?: string
    phone?: string
    preferredContact?: string
    isEmergencyContact?: boolean
  }
  createdAt?: string
  updatedAt?: string
  notesUpdatedAt?: string
}

/**
 * Transform flat Firestore student data into nested object structure
 * This function converts flat fields like addressStreet, guardianName, etc.
 * into nested objects like address.street, guardian.name, etc.
 */
export function transformStudentData(
  firestoreData: FirestoreStudentData,
): Student {
  const student: Student = {
    id: firestoreData.id,
    studentId: firestoreData.studentId,
    name: firestoreData.name,
    firstName: firestoreData.firstName,
    lastName: firestoreData.lastName,
    email: firestoreData.email,
    phone: firestoreData.phone,
    instrument: firestoreData.instrument,
    instruments: firestoreData.instruments,
    notes: firestoreData.notes,
    avatar: firestoreData.avatar,
    profilePicture: firestoreData.profilePicture,
    status: firestoreData.status,
    lessonType: firestoreData.lessonType,
    timeZone: firestoreData.timeZone,
    createdAt: firestoreData.createdAt,
    updatedAt: firestoreData.updatedAt,
    notesUpdatedAt: firestoreData.notesUpdatedAt,
  }

  // Transform address fields into nested object
  if (
    firestoreData.addressStreet ||
    firestoreData.addressApt ||
    firestoreData.addressCity ||
    firestoreData.addressState ||
    firestoreData.addressZip
  ) {
    student.address = {
      street: firestoreData.addressStreet,
      apt: firestoreData.addressApt,
      city: firestoreData.addressCity,
      state: firestoreData.addressState,
      zip: firestoreData.addressZip,
    }
  }

  // Transform guardian fields into nested object
  if (
    firestoreData.guardianName ||
    firestoreData.guardianEmail ||
    firestoreData.guardianPhone ||
    firestoreData.guardianPreferredContact ||
    firestoreData.guardianIsEmergencyContact !== undefined
  ) {
    student.guardian = {
      name: firestoreData.guardianName,
      email: firestoreData.guardianEmail,
      phone: firestoreData.guardianPhone,
      preferredContact: firestoreData.guardianPreferredContact,
      isEmergencyContact: firestoreData.guardianIsEmergencyContact,
    }
  }

  return student
}

/**
 * Get all students for a teacher from Firestore
 * Automatically transforms flat Firestore data into nested object structure
 */
export async function getTeacherStudents(
  teacherId: string,
): Promise<Student[]> {
  try {
    console.log('getTeacherStudents: Fetching students for teacher:', teacherId)
    // Query the students subcollection under the teacher document
    const studentsRef = collection(db, 'teachers', teacherId, 'students')
    const studentsSnapshot = await getDocs(studentsRef)
    console.log('getTeacherStudents: Found', studentsSnapshot.size, 'students')

    // Transform each document into the nested structure
    const students: Student[] = studentsSnapshot.docs.map((doc) => {
      const data = doc.data() as FirestoreStudentData
      // Log raw data for debugging
      console.log('Raw Firestore data for student:', doc.id, {
        hasAddressStreet: !!data.addressStreet,
        hasGuardianName: !!data.guardianName,
        addressStreet: data.addressStreet,
        guardianName: data.guardianName,
      })

      // Transform the data
      const transformedStudent = transformStudentData({
        ...data,
        id: doc.id,
      })

      // Log transformed data for debugging
      console.log('Transformed student data:', doc.id, {
        hasAddress: !!transformedStudent.address,
        hasGuardian: !!transformedStudent.guardian,
        address: transformedStudent.address,
        guardian: transformedStudent.guardian,
      })

      return transformedStudent
    })

    console.log(
      'getTeacherStudents: Successfully transformed',
      students.length,
      'students',
    )
    return students
  } catch (error) {
    console.error('getTeacherStudents: Error fetching students:', error)
    throw error
  }
}

/**
 * Get a single student by ID for a teacher
 * Automatically transforms flat Firestore data into nested object structure
 */
export async function getTeacherStudent(
  teacherId: string,
  studentId: string,
): Promise<Student | null> {
  try {
    console.log('getTeacherStudent: Fetching student:', {
      teacherId,
      studentId,
    })
    const students = await getTeacherStudents(teacherId)
    const student = students.find(
      (s) => s.id === studentId || s.studentId === studentId,
    )
    if (!student) {
      console.log('getTeacherStudent: Student not found')
      return null
    }
    console.log('getTeacherStudent: Found student with transformed data:', {
      hasAddress: !!student.address,
      hasGuardian: !!student.guardian,
    })
    return student
  } catch (error) {
    console.error('getTeacherStudent: Error fetching student:', error)
    throw error
  }
}
